/**
 * Every string the partner reads, in one place.
 *
 * English only today. Telugu is the reason this is a module and not inline
 * JSX: adding `te` means adding a second object of the same shape and picking
 * one by `partner.locale` — no hunting through components. Keep the keys
 * meaning-shaped ("shortfall"), not English-shaped ("youNeedMore"), so the
 * Telugu draft can restructure the sentence.
 */

/**
 * The version of the consent line below, sent with the registration and stored
 * against the account so "what were they actually told" is answerable later.
 *
 * Dated, like the go form's `partners-v1-2026-08` — and a different string,
 * because these are different words. Change `login.consent` and change this in
 * the same edit; `PARTNER_CONSENT_VERSION` in
 * `apps/api/src/routes/partner/auth.routes.ts` is the other half, and the API
 * refuses a version it does not recognise.
 */
export const PARTNER_CONSENT_VERSION = "partner-app-v1-2026-09";

/** The partner privacy page in the help centre, linked from that line. */
export const PRIVACY_URL = "https://help.interioring.com/partner/privacy/";

export const copy = {
	appName: "Interioring Partners",

	login: {
		title: "Partner sign in",
		subtitle: "We send a 6-digit code to your WhatsApp.",
		phoneLabel: "WhatsApp number",
		phonePlaceholder: "98765 43210",
		phoneHint: "The number you registered with.",
		phoneInvalid: "Enter a 10-digit Indian mobile number.",
		sendCode: "Send code",
		sending: "Sending…",
		// Deliberately says nothing about whether the number is registered —
		// the API always answers ok, and so does this screen.
		codeSent: "If that number is registered, a code is on its way.",
		codeLabel: "6-digit code",
		registerTitle: "One last thing",
		// Not "we have not seen this number before" — a contact we imported and
		// already messaged reaches this step too, having only never agreed.
		registerBody:
			"Before you are in, tell us who you are and check what you are agreeing to.",
		nameLabel: "Your name",
		firmLabel: "Your firm",
		optional: "Optional",
		// The consent act is creating the account, so the line sits beside the
		// button that does it and says plainly what it covers. Versioned by
		// PARTNER_CONSENT_VERSION above — change one, change the other.
		consent:
			"By creating your account you agree that we keep your name, number, the referrals you send and the payment details we need to pay you, and that we message you on WhatsApp about them. Reply STOP any time and we stop.",
		consentLink: "What we hold, and how to stop",
		registerCta: "Create my account",
		nameRequired: "Please tell us your name.",
		codeHint: "Check WhatsApp on this phone.",
		codeInvalid: "Enter the 6-digit code.",
		verify: "Sign in",
		verifying: "Checking…",
		verifyFailed: "That code did not work. Check it, or send a new one.",
		changeNumber: "Change number",
		resend: "Send a new code",
		resendIn: (seconds: number) => `Send a new code in ${seconds}s`,
		resent: "New code sent.",
	},

	dashboard: {
		// `name` is nullable — a prospect imported from a phone list has none
		// until they tell us. Fall back rather than greeting them as "null".
		greeting: (name: string | null) => (name ? `Hello, ${name}` : "Hello"),
		signOut: "Sign out",
		loading: "Loading your account…",
		loadFailed: "Could not load your account. Pull down to try again.",
		retry: "Try again",

		availableTitle: "Ready to withdraw",
		availableEmpty: "Nothing to withdraw yet.",
		withdraw: "Withdraw",
		shortfall: (short: string, threshold: string) =>
			`${short} more to reach the ${threshold} minimum withdrawal.`,
		payoutPending: (amount: string) =>
			`${amount} withdrawal requested. We are sending it.`,
		payoutPendingTo: (upiId: string) => `To ${upiId}`,

		heldTitle: "On hold",
		heldNone: "Nothing on hold.",
		heldClears: (date: string) => `Next ${date}`,
		heldWhy:
			"New earnings wait 5 days before you can withdraw them, in case a referral turns out to be a duplicate.",

		paidTitle: "Paid so far",
		totalTitle: "Earned, not yet paid",

		referralsTitle: "Your referrals",
		referralsEmpty:
			"No referrals yet. Add a contact and send them their free estimate — it takes under a minute.",
		/**
		 * FR-P-2.6 — a worked EXAMPLE, not an illustration.
		 *
		 * A partner who has referred nobody cannot picture what this screen
		 * becomes, and an empty box with a sentence under it does not tell
		 * them. A filled-in card does — clearly labelled, because a sample that
		 * could be mistaken for a real referral is worse than no sample.
		 */
		exampleTag: "Example",
		exampleName: "Priya Sharma",
		exampleWhere: "Gachibowli · 3BHK",
		exampleStatus: "We spoke to her — ₹100 earned",
		exampleCaption:
			"This is what a referral looks like once we have reached your contact. Yours will show the same line, with their name.",
		referredOn: (date: string) => `Added ${date}`,
		noPhone: "No number given",
		addReferral: "Add a referral",
		shareLink: "Share my link",

		/**
		 * FALLBACK ONLY. The API sends `label`/`hint` with every referral on the
		 * list and detail endpoints, and those are what the screen renders —
		 * FR-P-2.2 says no internal status string ever reaches a partner, and
		 * the safest way to keep that true is for the words to come from the
		 * same place as the state.
		 *
		 * These exist because `/api/partner/me` projects referrals without the
		 * labels, and a missing word would render the raw enum. Keep them in
		 * step with apps/api/src/lib/partners/timeline.ts.
		 */
		status: {
			draft: "Not sent yet",
			forwarded: "Waiting for them to message us",
			engaged: "They are talking to us",
			verified: "Confirmed",
			contacted: "With a professional",
			quoted: "With a professional",
			converted: "Converted",
			duplicate: "Already with us",
			rejected: "Not eligible",
			expired: "Expired",
			lost: "Not proceeding",
			opted_out: "Not proceeding",
		} as Record<string, string>,
		statusHint: {
			draft: "Tap to send them their free estimate.",
			forwarded: "We will let you know the moment they message us.",
			engaged: "We are checking a few details with them.",
			verified: "₹100 approved. It moves to your balance after the hold.",
			contacted: "We have introduced them to a professional.",
			quoted: "We have introduced them to a professional.",
			converted: "₹1,000 approved.",
			duplicate:
				"This contact reached us earlier, so there is no payout on this one.",
			rejected: "We could not take this one forward.",
			expired: "This was not sent within 3 days. You can add them again.",
			lost: "This one is not going ahead.",
			opted_out: "This one is not going ahead.",
		} as Record<string, string>,
	},

	/** Add a referral — the two-step screen. FR-P-3. */
	refer: {
		title: "Add a referral",
		subtitle:
			"We will not contact them. You send them the estimate yourself, from your own WhatsApp.",
		nameLabel: "Their name",
		namePlaceholder: "Ramesh",
		phoneLabel: "Their WhatsApp number",
		phoneHint: "10-digit mobile number.",
		societyLabel: "Society or project",
		societyPlaceholder: "My Home Apas",
		societyHint: "Optional, but it makes the message far more personal.",
		configLabel: "Home size",
		configOptional: "Optional",
		/** Keys are the lead store's BHK values (#1085). */
		configOptions: {
			"1": "1 BHK",
			"2": "2 BHK",
			"3": "3 BHK",
			"4P": "4 BHK or bigger",
			VILLA: "Villa",
		} as const,
		possessionLabel: "When do they get possession?",
		possessionHint:
			"If they have already moved in, pick the month they did.",
		possessionNotSure: "Not sure",
		consentLabel: "I have the homeowner's permission to share their number",
		submit: "Continue",
		submitting: "Saving…",

		// Step 2.
		readyTitle: (name: string) => `Send ${name} their estimate`,
		readyBody:
			"This opens your own WhatsApp with a message ready to send. Edit it however you like — it is your chat.",
		send: (name: string) => `Send ${name} their estimate`,
		sentAlready: "I have sent it",
		later: "I will send it later",
		draftWarning:
			"Nothing is sent until you tap the button. A referral you never send expires after 3 days.",

		duplicateTitle: "This contact is already with us",
		duplicateBody:
			"Someone sent us this number earlier, so there is no payout on this one. You can still send them the estimate if you like — it costs you nothing and they may thank you for it.",

		errors: {
			invalidPhone: "That does not look like a 10-digit mobile number.",
			consentRequired:
				"Please confirm you have their permission to share their number.",
			selfReferral:
				"That number belongs to a partner. You cannot refer yourself or another partner.",
			capDay: (limit: number) =>
				`You have added ${limit} referrals today, which is the daily limit. Try again tomorrow.`,
			capMonth: (limit: number) =>
				`You have added ${limit} referrals this month, which is the limit. It resets as your older ones age out.`,
			suspended:
				"Your account is on hold, so you cannot add referrals right now.",
		},
	},

	/** The open share link. FR-P-6. */
	share: {
		title: "Share your link",
		back: "Back",
		body: "Anyone who opens this and messages us is counted as your referral — you do not have to add them one by one.",
		linkLabel: "Your link",
		copy: "Copy link",
		copied: "Copied",
		sendOnWhatsApp: "Send on WhatsApp",
		shareText:
			"Getting your new home interiors done? Interioring gives you a free estimate and puts you in touch with a professional. No cost, no obligation:",
		loading: "Getting your link…",
		failed: "We could not get your link just now.",
		howTitle: "How it works",
		how: [
			"Send the link to a society group, or put it on your status.",
			"Anyone who opens it and messages us becomes your referral.",
			"It pays exactly the same as adding someone yourself.",
		],
	},

	/** One referral — the promise tracker. FR-P-5. */
	referral: {
		back: "All referrals",
		timelineTitle: "What has happened",
		nudge: (name: string) => `Remind ${name}`,
		nudgeHint: "You can send one reminder every 2 days, twice in total.",
		nudgeSpent: "You have sent both reminders for this one.",
		nudgeTooSoon: "You sent a reminder recently. Try again in a day or two.",
		resend: (name: string) => `Send ${name} their estimate`,
		earnedTitle: "Earned on this referral",
		nothingEarned: "Nothing earned on this one yet.",
		/**
		 * The transparency limit, said out loud rather than left as an absence.
		 * FR-P-5.3: the partner is paid for an introduction, and the homeowner
		 * never agreed to have their conversation shown to them.
		 */
		privacyNote:
			"We do not show what a homeowner tells us — their budget, their plans, or anything they say to our team. You will always see the status, and what you are owed.",
	},

	earning: {
		kind: {
			validated: "Validated",
			converted: "Went ahead",
			// Deliberately not "Clawback" — nobody outside an accounts team
			// reads that as anything but a threat. This is what actually
			// happened: something we paid for turned out not to count.
			clawback: "Taken back",
		} as const,
		state: {
			accrued: "On hold",
			released: "Ready",
			paid: "Paid",
			reversed: "Reversed",
		} as const,
		clearsOn: (date: string) => `Clears ${date}`,
	},

	/**
	 * FR-M-6 / FR-P-5: the enum, said in words the partner did not have to
	 * learn. Each one is written to be readable by the person it happened to —
	 * `fraud` in particular, which is an internal classification and must never
	 * be shown to a partner as an accusation they then have to argue with.
	 */
	reversalReason: {
		duplicate_referral:
			"Someone had already referred this contact before you did.",
		unreachable_contact: "We could not reach this contact.",
		consent_denied: "This contact asked us not to contact them.",
		operator_correction: "We made a mistake on our side and corrected it.",
		fraud:
			"We could not verify this one. Ask us on WhatsApp and we will explain.",
	} as const,

	clawback: {
		title: "Taken back",
		// Separate wording from `reversal` on purpose. A reversal is a credit
		// withdrawn before it was ever paid; this is money the partner already
		// has, and the single most important thing to say is that we are not
		// asking for it back.
		intro:
			"These were paid to you, and later turned out not to count. We will not ask you to return them — they come off your next payment instead.",
		unknownReason:
			"Reason not recorded. Ask us on WhatsApp and we will explain this one.",
	},

	reversal: {
		title: "Reversed earnings",
		// A balance that silently shrinks is how a support conversation becomes
		// an accusation. Every reversal gets a line, with a reason where we
		// have one and an honest admission where we do not.
		intro:
			"These were added to your balance and later taken back. Here is why.",
		unknownReason:
			"Reason not recorded. Ask us on WhatsApp and we will explain this one.",
	},

	/**
	 * FR-P-2.4 — the strip at the top of the dashboard.
	 *
	 * One line, one verb, and a number where there is one. These are prompts
	 * about the partner's OWN unfinished work, never marketing: each names
	 * something they started and can finish in a tap.
	 */
	actions: {
		sendDrafts: (n: number) =>
			n === 1
				? "You have 1 referral you never sent."
				: `You have ${n} referrals you never sent.`,
		sendDraftsCta: "Send it now",
		nudge: (n: number) =>
			n === 1
				? "1 contact has not replied yet. A reminder from you carries more weight than one from us."
				: `${n} contacts have not replied yet. A reminder from you carries more weight than one from us.`,
		nudgeCta: "Open it",
		addUpi: (amount: string) =>
			`${amount} is ready. Add the UPI id you want it sent to.`,
		addUpiCta: "Add UPI id",
		dismiss: "Not now",
	},

	/**
	 * FR-P-8. Read-only, and it points at a human (owner decision, 31 Aug 2026).
	 *
	 * Written for someone who is worried, not someone who is curious: the
	 * questions are in the order they get asked on the WhatsApp thread, and the
	 * two that come up most — "where did you get my number" and "how do I get
	 * off this" — are answered without a single hedge.
	 */
	help: {
		title: "How this works",
		back: "Back",

		howTitle: "The short version",
		howBody:
			"You introduce someone who is planning interiors. We contact them, and if they are genuinely planning, you earn ₹100. If they go ahead with a professional we match them to, you earn ₹1,000 more.",

		verifiedTitle: "What 'verified' means",
		verifiedBody:
			"Someone on our team reached your contact and confirmed they are real and planning interiors. It is not automatic and it is not a guess — a person spoke to them. Until that happens the referral sits as 'we are trying'.",

		payoutTitle: "When you get paid",
		payoutBody:
			"Every amount waits 5 days before you can withdraw it, so a duplicate or a wrong number can be corrected first. Once your available balance reaches ₹1,000 you can request a payout. We send it by UPI, by hand, on working days — not within minutes, and not overnight.",

		capsTitle: "Why there is a daily limit",
		capsBody:
			"New partners can add a few referrals a day. It is not about you: it is what stops someone flooding us with numbers they never spoke to, which would make every real introduction worth less. The limit lifts on its own once your referrals start reaching people.",

		numberTitle: "Where did you get my number?",
		numberSelf: (joined: string) =>
			`You gave it to us. You filled in the partner form on our site on ${joined}, and that is the only reason we have it.`,
		numberRecruited:
			"We found your business listed publicly as an interior or property professional in Hyderabad, and messaged you to ask if you wanted to join. You replied, which is why you have an account.",
		numberOperator:
			"Someone on our team added you after speaking with you directly. If that does not match your memory, tell us — we would rather check than assume.",

		dataTitle: "What we hold about you",
		dataBody:
			"Your name, your WhatsApp number, your firm if you gave one, the referrals you have sent, and the money we owe or have paid you. If you have given us a UPI id we hold that, and the last four digits of your PAN if you have sent it.",

		removeTitle: "Getting your data removed",
		/**
		 * FR-P-8.2 asks for one-tap deletion. There is no button (owner
		 * decision), so the page says so plainly rather than hiding the gap
		 * behind a form that emails somebody. A promise about a real person
		 * reading it is one we can keep today.
		 */
		removeBody:
			"Message us on WhatsApp and ask. A person reads it and removes what we can remove — we keep records of payments we have already made, because we are required to. There is no button for this yet; we would rather tell you that than send you to a form nobody reads.",
		removeCta: "Ask us to remove your data",
		removeMessage: "Hi, I would like my data removed from Interioring.",

		humanTitle: "Talk to a person",
		humanBody:
			"Anything on this page, anything about a referral, anything about money — the same thread you signed up on. We answer during working hours.",
		humanCta: "Message us on WhatsApp",
		humanMessage: "Hi, I have a question about my partner account.",
	},

	ledger: {
		/**
		 * FR-P-7.5. A partner arguing about a payment should not have to
		 * screenshot a phone, and the person who does their books cannot read a
		 * screenshot at all.
		 */
		download: "Download every line as a spreadsheet",
	},

	tds: {
		title: "We need your PAN to pay you",
		/**
		 * FR-M-7. Written to explain a legal obligation rather than announce a
		 * refusal: the partner has done nothing wrong, and the money is theirs.
		 * The threshold and their own total are both named, because "you have
		 * hit a limit" with no numbers is indistinguishable from an error.
		 */
		body: (fyPaid: string, threshold: string, fy: string) =>
			`You have been paid ${fyPaid} in ${fy}. Past ${threshold} in one financial year we have to record a PAN against the payment — it is a tax rule, not our own. Once yours is on file we release this.`,
		/** The dashboard's way in: the PAN is entered on the payout screen. */
		enterCta: "Enter your PAN",
		// The primary path (owner decision): typed here, kept as its last four.
		// WhatsApp stays as the fallback for a partner who would rather send it
		// to a person.
		panLabel: "Your PAN",
		panPlaceholder: "ABCDE1234F",
		panHint:
			"10 characters, as printed on the card. We keep only the last four.",
		panInvalid:
			"Enter the 10-character PAN as printed on the card, like ABCDE1234F.",
		save: "Save PAN",
		saving: "Saving…",
		saved: "PAN saved. You can withdraw now.",
		cta: "Send your PAN on WhatsApp",
		whatsappFallback: "Or send it to us on WhatsApp instead",
		// Prefilled so the partner does not have to explain themselves, and so
		// the operator reading the thread knows immediately what it is about.
		whatsappMessage:
			"Hi, I need to share my PAN so my payment can be released.",
	},

	payout: {
		title: "Withdraw",
		back: "Back",
		amountLabel: "You are withdrawing",
		upiLabel: "Your UPI ID",
		upiPlaceholder: "name@bank",
		upiHint: "We send the money here. Check it character by character.",
		upiInvalid: "Enter a UPI ID like name@bank.",
		submit: "Request withdrawal",
		submitting: "Requesting…",
		requested: "Withdrawal requested.",
		pendingTitle: "A withdrawal is already on the way",
		pendingBody: (amount: string, upiId: string) =>
			`${amount} to ${upiId}. You can request the next one once this is settled.`,
		belowTitle: "Not enough to withdraw yet",
		belowBody: (short: string, threshold: string) =>
			`You can withdraw once ${threshold} is ready. You need ${short} more.`,
		historyTitle: "Past withdrawals",
		historyEmpty: "No withdrawals yet.",
		payoutState: {
			pending: "On the way",
			sent: "Sent",
			failed: "Failed",
			cancelled: "Cancelled",
		} as const,
		reference: (ref: string) => `Ref ${ref}`,
	},

	errors: {
		network: "No connection. Check your internet and try again.",
		payoutPending: "You already have a withdrawal on the way.",
		belowThreshold: (short: string) => `You need ${short} more to withdraw.`,
		generic: "Something went wrong. Try again.",
	},
} as const;

export type Copy = typeof copy;
